<template>
  <v2-modal
    :show="show"
    title="Share Wishlist's Link"
    @close="$emit('close')"
  >
    <!-- Link ready -->
    <div
      v-if="shareLink && !loading && !error"
      class="v2-share-link"
    >
      <p class="v2-text-sm v2-text-center">
        Your shareable link is ready!
      </p>
      <p class="v2-text-sm v2-text-center v2-mb-4">
        You can copy it below:
      </p>
      <div class="v2-flex v2-items-center v2-gap-2">
        <v2-input
          :value="shareLink"
          :readonly="true"
          class="v2-flex-1"
        />
        <v2-button
          variant="outline"
          :disabled="isCopied"
          @click="copyLink"
        >
          {{ isCopied ? 'Copied!' : 'Copy' }}
        </v2-button>
      </div>
      <p class="v2-text-xs v2-text-muted v2-mt-3">
        This link can be shared publicly and allows others to view the selected products.
      </p>

      <!-- KP-3326 Path 4: use the line sheet in PLG outreach. Only shown when
           embedded in PLG, which is where the hand-off lands. Each button posts
           a LINE_SHEET_OUTREACH message with a destination ('sequence' |
           'template') and mode ('new' | 'existing'); PLG branches on both to
           open the matching drafting flow pre-seeded with the line sheet. -->
      <div
        v-if="inIframe"
        class="v2-linesheet-outreach"
      >
        <div class="v2-outreach-divider">
          <span>Or use it in your outreach</span>
        </div>
        <div class="v2-outreach-actions">
          <v2-button
            v-for="action in outreachActions"
            :key="`${action.destination}-${action.mode}`"
            variant="outline"
            size="sm"
            @click="handleOutreach(action.destination, action.mode)"
          >
            {{ action.label }}
          </v2-button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-else-if="loading"
      class="v2-share-link__loading"
    >
      <v2-spinner label="Generating link..." />
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="v2-share-link__error"
    >
      <span class="v2-text-destructive v2-text-sm">Failed to generate link</span>
    </div>
  </v2-modal>
</template>

<script>
import { toRef, watch } from '@vue/composition-api'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import { useShareLinkSelection } from '@/composables/useShareLinkSelection'
import { isInIframe } from '@/utils/iframe-detect'

// KP-3326 Path 4 outreach actions. Each row maps a button to the
// destination/mode pair PLG branches on. 'new' opens a fresh sequence/template
// pre-seeded with the line sheet; 'existing' lets the user pick one to add to.
const OUTREACH_ACTIONS = [
  { label: 'Create a sequence', destination: 'sequence', mode: 'new' },
  { label: 'Create a template', destination: 'template', mode: 'new' },
  { label: 'Add to existing sequence', destination: 'sequence', mode: 'existing' },
  { label: 'Add to existing template', destination: 'template', mode: 'existing' },
]

export default {
  name: 'V2ShareLinkSelectionModal',
  components: {
 V2Modal, V2Button, V2Input, V2Spinner,
},
  props: {
    show: { type: Boolean, default: false },
    wishlistId: { type: String, default: '' },
    wishlistName: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const wishlistIdRef = toRef(props, 'wishlistId')
    const sl = useShareLinkSelection(wishlistIdRef, toRef(props, 'wishlistName'))

    watch(() => props.show, val => {
      if (val) sl.getLink()
    })

    // Hand the link to PLG, then close — PLG opens its drafting flow. Only
    // close on success; on failure useInOutreach toasts and we keep the modal
    // open so the user can retry.
    function handleOutreach(destination, mode) {
      if (sl.useInOutreach(destination, mode)) {
        emit('close')
      }
    }

    return {
      ...sl, inIframe: isInIframe(), handleOutreach, outreachActions: OUTREACH_ACTIONS,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-share-link {
  &__loading,
  &__error {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.v2-text-center { text-align: center; }
.v2-text-muted { color: var(--v2-muted-foreground); }
.v2-text-destructive { color: var(--v2-destructive); }
.v2-flex-1 { flex: 1; }

.v2-linesheet-outreach {
  margin-top: 1.25rem;
}

.v2-outreach-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: var(--v2-muted-foreground);
  font-size: 0.75rem;
  margin-bottom: 0.75rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid var(--v2-border, rgba(0, 0, 0, 0.1));
  }

  span { padding: 0 0.75rem; }
}

.v2-outreach-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
</style>
